import type { ThemingProps } from '@chakra-ui/system'
import { useMemo } from 'react'

import { CompanyCheckboxGroupProvider } from './checkbox-context'
import type { UseCompanyCheckboxGroupProps } from './checkbox-types'
import { useCompanyCheckboxGroup } from './use-checkbox-group'

export interface CompanyCheckboxGroupProps
  extends UseCompanyCheckboxGroupProps,
  Omit<ThemingProps<'Checkbox'>, 'orientation'> {
  children?: React.ReactNode
}

/**
 * Used for multiple checkboxes which are bound in one group,
 * and it indicates whether one or more options are selected.
 *
 * @see Docs https://chakra-ui.com/checkbox
 */
export function CompanyCheckboxGroup(props: CompanyCheckboxGroupProps) {
  const { colorScheme, size, variant, children, isDisabled } = props
  const { value, onChange } = useCompanyCheckboxGroup(props)

  const group = useMemo(
    () => ({
      size,
      onChange,
      colorScheme,
      value,
      variant,
      isDisabled,
    }),
    [size, onChange, colorScheme, value, variant, isDisabled],
  )

  return <CompanyCheckboxGroupProvider value={group} children={children} />
}

CompanyCheckboxGroup.displayName = 'CompanyCheckboxGroup'
